import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import TagManager from 'react-gtm-module'
import css from 'styled-jsx/css'
import { useSiteInfo } from 'shared'
import { PrismicQuestionsSingle } from '../../prismic-types-d'
import { AAnswer } from '../atoms/a-answer'

type Props = {
  questions: PrismicQuestionsSingle['data']['body']
  currentQuestion: number
  answers: Array<Array<number>>
  onAdvance?: () => void
  onPrevious?: () => void
  onUpdate?: (currentQuestion: number, value: number, checked: boolean) => void
}

export const CQuestions = ({
  questions,
  currentQuestion,
  answers,
  onAdvance,
  onPrevious,
  onUpdate,
}: Props) => {
  const { t } = useTranslation('common')
  const { isUS, isUK } = useSiteInfo()

  const isMultiple = useMemo(() => {
    return questions[currentQuestion].primary.multiple_choice
  }, [questions])

  const pushDatalayer = () => {
    if (!isMultiple) {
      const selectedAnswerIndex = answers[currentQuestion][0]
      const selectedAnswerText =
        questions[currentQuestion].items[selectedAnswerIndex].answer_text
      const questionText =
        questions[currentQuestion].primary.question_text[0].text
      TagManager.dataLayer({
        dataLayer: {
          event: 'Quiz Question Answered',
          questionText,
          selectedAnswerText,
        },
      })
    } else if (isMultiple) {
      const selectedAnswerIndex = answers[currentQuestion]
      const selectedAnswerText: Array<string> = []
      selectedAnswerIndex.forEach((e) => {
        selectedAnswerText.push(questions[currentQuestion].items[e].answer_text)
      })
      const questionText =
        questions[currentQuestion].primary.question_text[0].text
      TagManager.dataLayer({
        dataLayer: {
          event: 'Quiz Question Answered',
          questionText,
          selectedAnswerText,
        },
      })
    }
  }

  const pushSegment = () => {
    if (!window.analytics) {
      return
    }
    if (!isMultiple) {
      const selectedAnswerIndex = answers[currentQuestion][0]
      const selectedAnswerText =
        questions[currentQuestion].items[selectedAnswerIndex].answer_text
      const questionText =
        questions[currentQuestion].primary.question_text[0].text

      window.analytics.track('Quiz Question Answered', {
        question_text: questionText,
        question_answer: selectedAnswerText,
        current_question: currentQuestion + 1,
        quiz_length: questions.length,
      })
    } else if (isMultiple) {
      const selectedAnswerIndex = answers[currentQuestion]
      const selectedAnswerText: Array<string> = []
      selectedAnswerIndex.forEach((e) => {
        selectedAnswerText.push(questions[currentQuestion].items[e].answer_text)
      })
      const questionText =
        questions[currentQuestion].primary.question_text[0].text

      window.analytics.track('Quiz Question Answered', {
        question_text: questionText,
        question_answer: selectedAnswerText,
        current_question: currentQuestion + 1,
        quiz_length: questions.length,
      })
    }
  }

  const nextQuestion = () => {
    pushDatalayer()
    pushSegment()
    if (onAdvance instanceof Function) {
      onAdvance()
    }
  }

  const prevQuestion = () => {
    if (onPrevious instanceof Function) {
      onPrevious()
    }
  }

  const updateAnswer = (index: number, checked: boolean) => {
    if (onUpdate instanceof Function) {
      onUpdate(currentQuestion, index, checked)
    }
  }

  return (
    <div
      className={classNames('w-full questions__container', {
        'max-w-2xl': !isUS && !isUK,
      })}
    >
      <fieldset>
        <div
          className={classNames('questions__main-grid', {
            'grid-rows-[120px_auto]': !isUS && !isUK,
            'mt-[50px]': (isUS || isUK),
          })}
        >
          <legend
            className={classNames('w-full', {
              'text-center text-primary-dark-teal text-2xl md:text-4xl md:text-gray-900 font-semibold':
                (isUS || isUK),
              'font-light text-neutral-white md:text-charcoal text-h6':
                !isUS && !isUK,
            })}
          >
            {questions[currentQuestion].primary.question_text[0].text}
          </legend>
          <div
            className={classNames({
              'flex flex-col': !isUS && !isUK,
              'flex flex-row flex-wrap justify-center gap-[20px] md:gap-[3.5vw] mt-6':
                (isUS || isUK),
            })}
          >
            {questions[currentQuestion].items.map((answer, answerIndex) => {
              return (
                <AAnswer
                  key={answerIndex}
                  className={!isUS && !isUK ? 'mt-4' : undefined}
                  type={
                    questions[currentQuestion].primary.multiple_choice
                      ? 'checkbox'
                      : 'radio'
                  }
                  tagline={answer.tagline}
                  icon={answer.icon}
                  label={answer.answer_text}
                  value={answerIndex.toString()}
                  selected={answers[currentQuestion].includes(answerIndex)}
                  onChange={(checked) => updateAnswer(answerIndex, checked)}
                />
              )
            })}
          </div>
        </div>
      </fieldset>
      {(!isUS && !isUK) ? (
        <div className="mt-6 questions__button-grid">
          <button
            className={classNames(
              'px-10 py-3 text-sm font-bold text-white uppercase bg-transparent border-2 border-white rounded-full focus:outline-none md:text-neutral-dark-gray md:border-neutral-dark-gray questions__next-button',
              {
                'opacity-0 scale-50': answers[currentQuestion].length === 0,
                'opacity-100 scale-100 transition-all duration-200 ease-out':
                  answers[currentQuestion].length > 0,
              }
            )}
            type="button"
            onClick={nextQuestion}
          >
            {currentQuestion === questions.length - 1
              ? t('get-my-results')
              : t('next-question')}
          </button>
          {currentQuestion !== 0 && (
            <button
              type="button"
              className={classNames(
                'py-2 mt-10 text-p-sm font-bold text-neutral-white uppercase focus:outline-none md:items-center md:text-primary-teal md:mt-0 questions__previous-button',
                {
                  'questions__previous-button--move-down':
                    answers[currentQuestion].length > 0,
                }
              )}
              onClick={prevQuestion}
            >
              <svg
                className="questions__previous-button-arrow inline"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  className="questions__previous-button-arrow-stroke"
                  d="M9.5 3.5L5 8L9.5 12.5"
                  strokeWidth="2"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t('previous-question')}
            </button>
          )}
        </div>
      ) : (
        <div className="flex justify-center items-center flex-col-reverse md:flex-row gap-5 mt-[65px] md:mt-[80px]">
          <button
            onClick={prevQuestion}
            className={classNames(
              'bg-transparent py-3 w-full md:max-w-[280px] rounded-full border border-primary-teal transition-all uppercase font-bold',
              {
                'bg-gray-200 border-gray-200 text-gray-700 cursor-not-allowed opacity-70':
                  currentQuestion === 0,
                'text-primary-dark-teal hover:bg-primary-teal hover:text-neutral-white':
                  currentQuestion !== 0,
              }
            )}
            disabled={Boolean(currentQuestion === 0)}
          >
            {t('previous-question')}
          </button>
          <button
            onClick={nextQuestion}
            className={classNames(
              'bg-primary-teal py-3 w-full md:max-w-[280px] rounded-full text-neutral-white hover:bg-primary-dark-teal transition-all uppercase font-bold',
              {
                'cursor-not-allowed opacity-70': Boolean(
                  answers[currentQuestion].length === 0
                ),
              }
            )}
            disabled={Boolean(answers[currentQuestion].length === 0)}
          >
            {currentQuestion === questions.length - 1
              ? t('get-my-results')
              : t('next-question')}
          </button>
        </div>
      )}
      <style jsx>{styles}</style>
    </div>
  )
}

const styles = css`
  .questions__main-grid {
    display: grid;
    gap: 1.5rem;
  }

  @media screen and (max-width: 1112px) {
    .questions__main-grid {
      grid-template-rows: auto auto;
    }
  }

  .questions__main-grid > legend {
    grid-row-start: 1;
    align-self: end;
    justify-self: end;
  }
  .questions__main-grid > div {
    grid-row-start: 2;
  }
  .question__label {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 12px 16px;
    font-weight: semibold;
    border: 2px solid #57606c;
    color: #57606c;
    cursor: pointer;
    transition: all 0.3s ease-in-out;
    border-radius: 0.25rem;
  }

  .question__label:last-child {
    margin-bottom: 0;
  }

  .next-button-enter-active {
    animation: scale-in-center 0.3s ease both;
  }

  /* Button Grid Layout */

  .questions__button-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: 54px;
  }

  .questions__next-button {
    grid-column-start: 1;
    justify-self: start;
    align-self: end;
  }

  .questions__previous-button {
    grid-column-start: 2;
    justify-self: end;
    align-self: center;
  }

  [dir='rtl'] .questions__previous-button-arrow {
    transform: rotate(180deg);
  }

  .questions__previous-button-arrow-stroke {
    stroke: #10a8ba;
  }

  @media screen and (max-width: 768px) {
    .questions__previous-button-arrow-stroke {
      stroke: #fff;
    }
  }

  @media screen and (max-width: 1112px) {
    .questions__button-grid {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: 60px 60px;
      height: unset;
    }

    .questions__next-button {
      grid-column-start: 1;
      grid-row-start: 1;
      justify-self: auto;
      letter-spacing: 0.05rem;
    }

    /* Animation for Previous Button shift on mobile */
    .questions__previous-button {
      grid-column-start: 1;
      grid-row-start: 2;
      transition: all 0.3s ease-in-out;
      margin-top: -50px;
      /* justify-self: start */
      justify-self: center;
      align-self: start;
      letter-spacing: 0.05rem;
    }

    .questions__previous-button--move-down {
      margin-top: 24px;
    }
  }
`
